/**
 * Safely retrieves and parses a JSON value from localStorage.
 * Returns the fallback value if:
 * - The key doesn't exist
 * - The value is invalid JSON
 * - An error occurs during parsing
 *
 * @param key - The localStorage key to retrieve
 * @param fallback - The value to return if retrieval/parsing fails
 * @returns The parsed value or the fallback value
 */
export function getLocalStorageItem<T>(key: string, fallback: T): T {
  try {
    const item = localStorage.getItem(key);
    if (item === null) {
      return fallback;
    }
    return JSON.parse(item) as T;
  } catch (error) {
    console.error(`Error parsing localStorage item ${key}:`, error);

    return fallback;
  }
}

/**
 * Safely sets a JSON value in localStorage.
 * Silently fails if:
 * - Stringification fails
 * - localStorage is full
 * - localStorage is not available
 *
 * @param key - The localStorage key to set
 * @param value - The value to store (will be JSON stringified)
 */
export function setLocalStorageItem<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Error setting localStorage item ${key}:`, error);
  }
}

/**
 * Safely retrieves and parses a JSON value from sessionStorage.
 * Same semantics as getLocalStorageItem but tab-scoped.
 */
export function getSessionStorageItem<T>(key: string, fallback: T): T {
  try {
    const item = sessionStorage.getItem(key);
    if (item === null) {
      return fallback;
    }
    return JSON.parse(item) as T;
  } catch (error) {
    console.error(`Error parsing sessionStorage item ${key}:`, error);

    return fallback;
  }
}

/**
 * Safely sets a JSON value in sessionStorage.
 * Same semantics as setLocalStorageItem but tab-scoped.
 */
export function setSessionStorageItem<T>(key: string, value: T): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Error setting sessionStorage item ${key}:`, error);
  }
}
